import {
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
    selector: '[hamRadio]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: HamRadioDirective,
            multi: true,
        },
    ],
})
export class HamRadioDirective implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() label: string | null = null;
    @Input() value: any;
    @Input() name: string;
    @Input() selected: boolean = false;

    @Output() change = new EventEmitter<any>();

    private wrapperElement: HTMLElement;
    private radioElement: HTMLElement;
    private labelElement: HTMLElement;

    private listeners: (() => void)[] = [];
    private onChange: (_: any) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private el: ElementRef, private renderer: Renderer2) {}

    ngOnInit() {
        // Create a wrapper element
        this.wrapperElement = this.renderer.createElement('div');
        this.renderer.addClass(this.wrapperElement, 'ham-radio-wrapper');
        this.renderer.insertBefore(this.el.nativeElement.parentNode, this.wrapperElement, this.el.nativeElement);

        // Create the radio input
        this.radioElement = this.renderer.createElement('input');
        this.renderer.setAttribute(this.radioElement, 'type', 'radio');
        this.renderer.setAttribute(this.radioElement, 'name', this.name);
        this.renderer.setAttribute(this.radioElement, 'value', this.value);
        this.renderer.addClass(this.radioElement, 'ham-radio-input');
        this.renderer.appendChild(this.wrapperElement, this.radioElement);

        // Create the label
        if (this.label) {
            this.labelElement = this.renderer.createElement('label');
            const text = this.renderer.createText(this.label);
            this.renderer.appendChild(this.labelElement, text);
            this.renderer.addClass(this.labelElement, 'ham-radio-label');
            this.renderer.appendChild(this.wrapperElement, this.labelElement);
        }

        // Set the initial selected state
        if (this.selected) {
            this.renderer.setProperty(this.radioElement, 'checked', true);
        }

        // Add event listener for changes
        this.listeners.push(
            this.renderer.listen(this.radioElement, 'change', () => {
                this.onChange(this.value);
                this.change.emit(this.value);
            }),
        );
    }

    ngOnDestroy() {
        if (this.wrapperElement && this.wrapperElement.parentNode) {
            this.renderer.removeChild(this.wrapperElement.parentNode, this.wrapperElement);
        }

        this.listeners.forEach((listener) => listener());
    }

    writeValue(value: any): void {
        const isChecked = value === this.value;
        this.renderer.setProperty(this.radioElement, 'checked', isChecked);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.renderer.setProperty(this.radioElement, 'disabled', isDisabled);
    }

    @HostListener('blur')
    onBlur() {
        this.onTouched();
    }
}
